// Aba "Leitura da IA" do quadro quando ainda não há leitura (feature 161): diz o que a aba vai mostrar,
// quando chega a primeira e traz um exemplo (marcado como tal, com números fictícios e sem nome de gente).
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import GroupsIcon from '@mui/icons-material/Groups';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { __t } from '../../i18n/t';
import { COLORS } from '../dashboards/shared';
import { BoardPeriod, NeverReason, OutlookKind, dayMonth, readingOutlook } from './readingOutlook';

const containerSx = {
  maxWidth: 920, display: 'flex', flexDirection: 'column' as const, gap: 2,
  borderRadius: 3, p: { xs: 1.75, sm: 2.5 },
  background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(168,85,247,0.08))',
  border: '1px solid rgba(139,92,246,0.22)',
};

// Só o que a leitura de fato produz (readings.ts/facts.ts): desempenho na semana, frentistas que bateram, dia mais fraco,
// e a recomendação do código (primeira semana só observa; da segunda em diante, manter/subir/reduzir).
const WILL_SEE: { icon: React.ElementType; text: string }[] = [
  { icon: QueryStatsIcon, text: 'Como a equipe foi em relação à meta e em quantos dias ela foi batida' },
  { icon: GroupsIcon, text: 'Quantos frentistas bateram a meta e em que dia da semana a equipe rende menos' },
  { icon: TrackChangesIcon, text: 'Se a meta está fácil ou difícil demais. A partir da 2ª semana, sugerimos manter, subir ou reduzir' },
];

const Example = () => (
  <Box sx={{
    position: 'relative', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 0.75, borderRadius: 3, p: 1.75,
    bgcolor: 'rgba(255,255,255,0.5)', border: '1.5px dashed rgba(100,116,139,0.5)', filter: 'grayscale(0.3)', opacity: 0.92,
  }}>
    <Chip
      size="small"
      label={__t('Exemplo')}
      sx={{ position: 'absolute', top: 10, right: 10, height: 20, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', bgcolor: 'rgba(100,116,139,0.16)', color: '#475569' }}
    />
    <Typography sx={{ fontSize: 12, color: COLORS.muted, pr: 9 }}>{__t('Leitura da semana, com números de exemplo')}</Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#475569' }}>{__t('A equipe bateu a meta em 68% dos dias')}</Typography>
    {[
      { icon: QueryStatsIcon, text: __t('5 frentistas bateram a meta em metade dos dias ou mais') },
      { icon: ArrowForwardIcon, text: __t('Reforce a equipe na terça, o dia mais fraco da semana') },
      { icon: TrackChangesIcon, text: __t('Mantenha a meta: ela está na faixa certa') },
    ].map(({ icon: Icon, text }) => (
      <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        <Icon sx={{ fontSize: 15, color: COLORS.faint, mt: 0.2, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{text}</Typography>
      </Box>
    ))}
    <Chip size="small" variant="outlined" label={__t('Manter a meta')} sx={{ alignSelf: 'flex-start', mt: 0.5, color: '#475569', borderColor: 'rgba(100,116,139,0.5)' }} />
  </Box>
);

const BoardReadingEmpty: React.FC<{ board: BoardPeriod }> = ({ board }) => {
  const outlook = readingOutlook(board, new Date());
  const hasPreview = outlook.kind !== OutlookKind.never;
  const data = outlook.kind === OutlookKind.never ? '' : dayMonth(outlook.day);

  let title: string;
  let subtitle: string;
  if (outlook.kind === OutlookKind.upcoming) {
    title = __t('Sua primeira análise chega na segunda, %{data}', { data });
    subtitle = __t('Volte nesse dia para ver como a equipe foi na última semana fechada, de segunda a domingo.');
  } else if (outlook.kind === OutlookKind.noData) {
    title = __t('Ainda sem dados para analisar a última semana');
    subtitle = __t('Tentamos de novo na segunda, %{data}. Só entram abastecimentos de frentistas identificados pela tag.', { data });
  } else {
    title = __t('Este quadro não terá análise semanal');
    subtitle = outlook.reason === NeverReason.targets
      ? __t('A análise semanal acompanha metas de quantidade, fidelização e volume.')
      : __t('A análise precisa de uma semana, de segunda a domingo, com o quadro ativo em pelo menos 5 dias.');
  }

  return (
    <Box sx={containerSx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', boxShadow: '0 4px 12px rgba(35,35,35,0.08)' }}>
          <EventAvailableIcon sx={{ color: COLORS.brand }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: COLORS.ink, lineHeight: 1.3 }}>{title}</Typography>
          <Typography sx={{ fontSize: 13, color: '#334155', mt: 0.5 }}>{subtitle}</Typography>
        </Box>
      </Box>

      {hasPreview && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.muted }}>
              {__t('O que você vai ver toda segunda-feira')}
            </Typography>
            {WILL_SEE.map(({ icon: Icon, text }) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Icon sx={{ fontSize: 18, color: COLORS.brand, mt: 0.15, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13.5, color: COLORS.ink }}>{__t(text)}</Typography>
              </Box>
            ))}
          </Box>
          <Example />
        </>
      )}
    </Box>
  );
};

export default BoardReadingEmpty;
